// =============================================================================
// hooks/sales/useContactAttempts.ts — Leitura e mutações de tentativas de
// contato comercial (`contact_attempts`, F34-S03, doc 26 §4.3/§9).
//
// Mesmo padrão de hooks/sales/useAppointments.ts + features/sales/agenda/hooks.ts:
// camada de dados fora da árvore visual, reutilizável por qualquer superfície
// (CRM F34-S05, futura operação de campanha F35). Só GET + criação/desfecho —
// nenhuma edição/cancelamento de tentativa existe na API (doc 26 §9).
//
// Endpoints (apps/api/src/modules/sales/attempts/routes.ts):
//   GET  /api/sales/contact-attempts             — lista (filtros + paginação)
//   POST /api/sales/contact-attempts              — criar tentativa
//   POST /api/sales/contact-attempts/:id/outcome  — registrar desfecho
//
// A criação de uma tentativa já grava um resumo genérico na timeline única
// do lead (`interactions`, ver header de apps/api/.../attempts/service.ts) —
// mas SEM o desfecho, que só existe depois do segundo POST. Por isso
// `useLeadContactAttemptsTimeline` busca a lista de tentativas diretamente
// (com `outcomeCode` já preenchido) para o merge de timeline do CRM
// (features/crm/CrmDetailPage.tsx) mostrar o desfecho legível — mesma
// estratégia de `hooks/crm/useLeadAppointments.ts` para compromissos.
// =============================================================================

import type {
  ContactAttempt,
  ContactAttemptCreateBody,
  ContactAttemptListResponse,
  ContactAttemptOutcomeBody,
  ContactAttemptOutcomeResponse,
} from '@elemento/shared-schemas';
import {
  ContactAttemptListResponseSchema,
  ContactAttemptOutcomeResponseSchema,
  ContactAttemptSchema,
} from '@elemento/shared-schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api';

export type {
  ContactAttempt,
  ContactAttemptCreateBody,
  ContactAttemptOutcomeBody,
  ContactAttemptOutcomeResponse,
};

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const contactAttemptKeys = {
  all: ['sales', 'contact-attempts'] as const,
  leadTimeline: (leadId: string) => ['sales', 'contact-attempts', 'lead-timeline', leadId] as const,
};

// ---------------------------------------------------------------------------
// Leitura — GET /api/sales/contact-attempts?leadId=...
// ---------------------------------------------------------------------------

async function fetchLeadContactAttempts(leadId: string): Promise<ContactAttemptListResponse> {
  const qs = new URLSearchParams();
  qs.set('leadId', leadId);
  qs.set('sortBy', 'startedAt');
  qs.set('sortOrder', 'desc');
  qs.set('limit', '50');
  const raw = await api.get<unknown>(`/api/sales/contact-attempts?${qs.toString()}`);
  return ContactAttemptListResponseSchema.parse(raw);
}

export function useLeadContactAttemptsTimeline(
  leadId: string,
  opts: { enabled?: boolean } = {},
): { attempts: ContactAttempt[]; isLoading: boolean } {
  const enabled = (opts.enabled ?? true) && leadId.length > 0;

  const { data, isLoading } = useQuery({
    queryKey: contactAttemptKeys.leadTimeline(leadId),
    queryFn: () => fetchLeadContactAttempts(leadId),
    enabled,
    staleTime: 15_000,
  });

  return { attempts: data?.data ?? [], isLoading: enabled && isLoading };
}

// ---------------------------------------------------------------------------
// Criar — POST /api/sales/contact-attempts
// ---------------------------------------------------------------------------

async function createContactAttempt(body: ContactAttemptCreateBody): Promise<ContactAttempt> {
  const raw = await api.post<unknown>('/api/sales/contact-attempts', body);
  return ContactAttemptSchema.parse(raw);
}

export function useCreateContactAttempt(): {
  createAsync: (body: ContactAttemptCreateBody) => Promise<ContactAttempt>;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: createContactAttempt,
    onSuccess: (attempt) => {
      void queryClient.invalidateQueries({
        queryKey: contactAttemptKeys.leadTimeline(attempt.leadId),
      });
    },
  });

  return { createAsync: (body) => mutation.mutateAsync(body) };
}

// ---------------------------------------------------------------------------
// Desfecho — POST /api/sales/contact-attempts/:id/outcome
// ---------------------------------------------------------------------------

async function recordAttemptOutcome(
  id: string,
  body: ContactAttemptOutcomeBody,
): Promise<ContactAttemptOutcomeResponse> {
  const raw = await api.post<unknown>(
    `/api/sales/contact-attempts/${encodeURIComponent(id)}/outcome`,
    body,
  );
  return ContactAttemptOutcomeResponseSchema.parse(raw);
}

export function useRecordAttemptOutcome(): {
  recordAsync: (input: {
    id: string;
    body: ContactAttemptOutcomeBody;
  }) => Promise<ContactAttemptOutcomeResponse>;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: ContactAttemptOutcomeBody }) =>
      recordAttemptOutcome(id, body),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({
        queryKey: contactAttemptKeys.leadTimeline(response.attempt.leadId),
      });
      // Um outcome que bloqueia contato grava contact_preferences na mesma
      // transação (doc 26 §9) — invalida a leitura de preferências do lead
      // para o ContactRestrictionBanner refletir sem precisar de F5.
      void queryClient.invalidateQueries({ queryKey: ['sales', 'contact-preferences'] });
      // Quando o outcome exige retorno, o servidor cria um novo compromisso
      // na mesma transação — mesmas chaves que ScheduleAppointmentDialog
      // invalida ao agendar (features/crm/CrmDetailPage.tsx onScheduled).
      if (response.nextAppointmentId) {
        void queryClient.invalidateQueries({ queryKey: ['crm', 'lead-next-actions'] });
        void queryClient.invalidateQueries({
          queryKey: ['crm', 'lead-appointments-timeline', response.attempt.leadId],
        });
        void queryClient.invalidateQueries({ queryKey: ['crm', 'due-lead-ids'] });
      }
    },
  });

  return { recordAsync: (input) => mutation.mutateAsync(input) };
}
